<div class="nt-root" [ngStyle]="themeVars"
  [class.nt-no-column-lines]="lineFlagOff('showColumnLines')" [class.nt-no-row-lines]="flag('hideRowLines')">
  <header class="nt-head" *ngIf="config.title || config.subtitle || level(0)?.actions?.length || rowSaveOf(0)">
    <div>
      <h3 class="nt-title" *ngIf="config.title">{{ config.title }}</h3>
      <p class="nt-subtitle" *ngIf="config.subtitle">{{ config.subtitle }}</p>
    </div>
    <!-- One right-aligned group: Save sits beside the buttons and leaves no gap when it goes. -->
    <div class="nt-head-tools">
      <ng-container *ngTemplateOutlet="actionsTpl; context: { levelIndex: 0, parentRow: null, parentKey: '', rows: rows }"></ng-container>
      <ng-container *ngTemplateOutlet="saveTpl; context: { levelIndex: 0, parentKey: '', rows: rows }"></ng-container>
    </div>
  </header>

  <div class="nt-empty" *ngIf="!config.levels.length">Add at least one level with columns to build the table.</div>
  <div class="nt-empty" role="status" *ngIf="config.levels.length && loading">Loading…</div>
  <div class="nt-empty is-error" role="alert" *ngIf="loadError">{{ loadError }}</div>

  <ng-container *ngIf="config.levels.length && !loading">
    <ng-container *ngTemplateOutlet="tableTpl; context: { levelIndex: 0, rows: rows, parentKey: '' }"></ng-container>
  </ng-container>
</div>

<!-- One level's table. Expanded rows render the next level through this same template. -->
<ng-template #tableTpl let-levelIndex="levelIndex" let-rows="rows" let-parentKey="parentKey">
  <div class="nt-table-wrap" [class.is-root]="levelIndex === 0" [attr.data-nt-level]="levelIndex" [style.--nt-sticky-top]="stickyTop(levelIndex)" [style.--nt-sticky-z]="20 - levelIndex">
    <table
      class="nt-table"
      [class.is-fixed-header-width]="headerWidth(levelIndex) !== null"
      [style.width]="tableWidth(levelIndex)"
      [class.is-dense]="level(levelIndex)?.dense"
      [style.--nt-th-size]="headerSizeVar(levelIndex)"
      [style.--nt-td-size]="cellSizeVar(levelIndex)"
      [style.fontFamily]="levelFontFamily(levelIndex)"
      [style.--nt-lvl-header-bg]="levelColorVar(levelIndex, 'HeaderBackground')"
      [style.--nt-lvl-header-color]="levelColorVar(levelIndex, 'HeaderColor')"
      [style.--nt-lvl-th-height]="levelPxVar(levelIndex, 'HeaderHeight')"
      [style.--nt-lvl-row-height]="levelPxVar(levelIndex, 'RowHeight')"
    >
      <thead>
        <tr>
          <!-- A level set to "Sort by clicking a header": ascending, descending, then back to its configured order. -->
          <th
            *ngFor="let column of columns(levelIndex)"
            [style.width]="columnWidth(column, levelIndex)"
            [style.min-width]="columnWidth(column, levelIndex)"
            [style.max-width]="columnWidth(column, levelIndex)"
            [class]="'is-' + (column.align || 'left')"
            [class.is-sortable]="headerSortable(levelIndex, column)"
            [class.is-wrap]="cellConfig(column)?.wrap === true"
            [attr.aria-sort]="sortIndicator(levelIndex, column) === 'asc' ? 'ascending' : sortIndicator(levelIndex, column) === 'desc' ? 'descending' : null"
            (click)="toggleSort(levelIndex, column)"
          >{{ headerLabel(column) }}<span class="nt-sort" *ngIf="sortIndicator(levelIndex, column) as dir">{{ dir === 'asc' ? '▲' : '▼' }}</span></th>
        </tr>
      </thead>
      <tbody>
        <!-- Shown in sort order; a row's key stays its place in `rows` (expand / edit / save state). -->
        <ng-container *ngFor="let row of sortedRows(rows, levelIndex); let index = index; trackBy: trackIndex">
          <ng-container *ngIf="rowKey(parentKey, rows.indexOf(row)) as key">
            <tr class="nt-row" [class.is-expanded]="isExpanded(key)">
              <td
                *ngFor="let column of columns(levelIndex); let first = first"
                [class]="'is-' + (column.align || 'left')"
                [class.is-mono]="column.mono"
                [class.is-wrap]="cellConfig(column)?.wrap === true"
                [ngStyle]="cellStyle(column, row, levelIndex)"
                [attr.title]="cellTooltip(column, row) || navTitle(column)"
                [class.is-clickable]="flag('clickToFilter') || hasNavigation(column)"
                [class.is-nav]="hasNavigation(column)"
                (click)="onValueClick(levelIndex, column, row, $event, key)"
              >
                <div class="nt-cell" [class.is-pair]="isPaired(column)">
                  <button
                    *ngIf="column.field === expanderField(levelIndex) && isExpandable(row, levelIndex)"
                    type="button"
                    class="nt-toggle"
                    [class.is-serial]="level(levelIndex)?.serialNumbers"
                    (click)="toggle(key, row, levelIndex)"
                    [attr.aria-expanded]="isExpanded(key)"
                    [attr.aria-label]="isExpanded(key) ? 'Collapse row' : 'Expand row'"
                  >
                    <ng-container *ngIf="level(levelIndex)?.serialNumbers; else expandIcon">{{ index + 1 }}</ng-container>
                    <ng-template #expandIcon><mat-icon>{{ isExpanded(key) ? 'expand_less' : 'expand_more' }}</mat-icon></ng-template>
                  </button>
                  <span class="nt-serial-number" *ngIf="column.field === expanderField(levelIndex) && level(levelIndex)?.serialNumbers && !isExpandable(row, levelIndex)">{{ index + 1 }}</span>

                  <ng-container *ngIf="!(column.field === expanderField(levelIndex) && column.type === 'index' && isExpandable(row, levelIndex))">
                    <!-- Cell kinds: the shared display kinds (badge / data bar /
                         progress / trend / colour dot / link) + the nested
                         editors (input / dropdown), all resolved from the ONE
                         generic cell configuration (nested-cell-config.utils). -->
                    <ng-container [ngSwitch]="cellKind(column)">
                      <span *ngSwitchCase="'badge'" class="nt-badge" [ngStyle]="badgeStyle(column, row)">
                        <span class="nt-cfg-icon" *ngIf="cellIcon(column, row)">{{ cellIcon(column, row) }}</span>{{ badgeText(column, row, index) }}
                      </span>
                      <span *ngSwitchCase="'databar'" class="nt-cfg-databar-wrap">
                        <span class="nt-cfg-databar" [style.width.%]="cellPercent(column, row, levelIndex)" [style.background]="cellAccent(column, row)"></span>
                        <span class="nt-cfg-databar-val">{{ display(column, row, index) }}</span>
                      </span>
                      <span *ngSwitchCase="'progress'" class="nt-cfg-progress">
                        <span class="nt-cfg-progress-fill" [style.width.%]="cellPercent(column, row, levelIndex)" [style.background]="cellAccent(column, row)"></span>
                        <span class="nt-cfg-progress-label">{{ display(column, row, index) }}</span>
                      </span>
                      <span *ngSwitchCase="'trend'" class="nt-cfg-trend"><span class="nt-cfg-icon">{{ cellIcon(column, row) }}</span>{{ display(column, row, index) }}</span>
                      <span *ngSwitchCase="'icon'" class="nt-cfg-iconcell">
                        <span class="nt-cfg-dot" [style.background]="cellAccent(column, row)"></span>
                        <span class="nt-cfg-icon" *ngIf="cellIcon(column, row)">{{ cellIcon(column, row) }}</span>{{ display(column, row, index) }}
                      </span>
                      <span *ngSwitchCase="'buttons'" class="tm-cell-buttons">
                        <button *ngFor="let button of cellConfig(column)?.buttons ?? []" type="button"
                          class="tm-cell-button" [attr.data-tone]="button.tone || 'blue'" [class.is-filled]="button.variant === 'filled'"
                          (click)="onCellButtonClick(button, levelIndex, column, row, key, $event)">{{ button.label }}</button>
                      </span>
                      <a *ngSwitchCase="'link'" class="nt-cfg-link" [tableCellNavigation]="cellConfig(column)" [navigationRow]="navigationRow(key, row)" [navigationField]="column.field" [navigationValue]="row[column.field]" [navigationDefaultKey]="navDefaultKey(levelIndex, column)" [navigationHref]="cellHref(column, row) || '#'" target="_blank" rel="noopener" (click)="$event.stopPropagation()">
                        <span class="nt-cfg-icon" *ngIf="cellIcon(column, row)">{{ cellIcon(column, row) }}</span>{{ display(column, row, index) }}
                      </a>

                      <!-- Editors: ONE box per row key — a paired column
                           (field + secondField) shows two, "6% - 10%", and
                           each edit reports the key it changed. -->
                      <ng-container *ngSwitchCase="'input'">
                        <ng-container *ngFor="let field of cellFields(column); let fi = index">
                          <span class="nt-pair-sep" *ngIf="fi">{{ separator(column) }}</span>
                          <label class="nt-editor" [class.is-invalid]="isInvalid(column, row, field)" [class.is-out-of-range]="isOutOfRange(column, row, field)" [class.is-readonly]="column.readOnly">
                            <span class="nt-adorn" *ngIf="editorPrefix(column)">{{ editorPrefix(column) }}</span>
                            <input
                              type="text"
                              [attr.inputmode]="column.numericOnly || column.inputType === 'number' ? 'decimal' : null"
                              [value]="editorValue(column, row, field)"
                              [style.width.ch]="isPaired(column) ? editorWidthCh(column, row, field) : null"
                              [ngStyle]="editorStyle(column, row)"
                              [placeholder]="placeholder(column) || '—'"
                              [readOnly]="!!column.readOnly"
                              [attr.aria-readonly]="column.readOnly ? 'true' : null"
                              (keydown)="onNumericKeydown($event, column)"
                              (input)="sanitizeNumericInput($event, column)"
                              (change)="onEditorChange($event, levelIndex, key, row, column, field)"
                              [attr.title]="boundsHint(column, row)"
                              [attr.aria-label]="fi ? column.label + ' (' + field + ')' : column.label"
                            />
                            <span class="nt-adorn" *ngIf="editorSuffix(column)">{{ editorSuffix(column) }}</span>
                            <!-- Out of its min / max range: the column's "Limit message", only when one was written. -->
                            <span class="nt-range-msg" role="alert" *ngIf="column.rangeMessage && isOutOfRange(column, row, field)">{{ rangeMessage(column, row, field) }}</span>
                          </label>
                          <!-- The limits and where each came from: "Min 405 (Current Price -10%)". -->
                          <span class="nt-bounds" *ngIf="!fi && column.showBounds === true && (column.minValue || column.maxValue) && boundsHint(column, row) as hint">{{ hint }}</span>
                        </ng-container>
                      </ng-container>

                      <ng-container *ngSwitchCase="'dropdown'">
                        <ng-container *ngFor="let field of cellFields(column); let fi = index">
                          <span class="nt-pair-sep" *ngIf="fi">{{ separator(column) }}</span>
                          <select
                            class="nt-select"
                            [class.is-invalid]="isInvalid(column, row, field)"
                            [class.is-placeholder]="!editorValue(column, row, field)"
                            [class.is-readonly]="column.readOnly"
                            [disabled]="!!column.readOnly"
                            [ngStyle]="editorStyle(column, row)"
                            (change)="onEdit(levelIndex, key, row, column, $any($event.target).value, field)"
                            [attr.aria-label]="fi ? column.label + ' (' + field + ')' : column.label"
                          >
                            <option value="" [selected]="!editorValue(column, row, field)">{{ placeholder(column) || 'Select...' }}</option>
                            <option
                              *ngFor="let option of options(column, levelIndex, row, field)"
                              [value]="option.value"
                              [selected]="option.value === editorValue(column, row, field)"
                            >{{ option.label }}</option>
                          </select>
                        </ng-container>
                      </ng-container>

                      <span *ngSwitchDefault><span class="nt-cfg-icon" *ngIf="cellIcon(column, row)">{{ cellIcon(column, row) }}</span>{{ display(column, row, index) }}</span>
                    </ng-container>
                  </ng-container>
                </div>
              </td>
            </tr>

            <tr class="nt-detail" *ngIf="isExpanded(key) && isExpandable(row, levelIndex)">
              <td [attr.colspan]="rowSpan(levelIndex)">
                <section class="nt-panel">
                  <header class="nt-panel-head">
                    <h4 class="nt-panel-title" [ngStyle]="levelTitleStyle(levelIndex + 1)">{{ level(levelIndex + 1)?.title }}</h4>
                    <div class="nt-head-tools">
                      <ng-container *ngTemplateOutlet="actionsTpl; context: { levelIndex: levelIndex + 1, parentRow: row, parentKey: key, rows: children(row, levelIndex) }"></ng-container>
                      <ng-container *ngTemplateOutlet="saveTpl; context: { levelIndex: levelIndex + 1, parentKey: key, rows: children(row, levelIndex) }"></ng-container>
                    </div>
                  </header>
                  <div class="nt-empty" role="status" *ngIf="childState(key) === 'loading'">Loading…</div>
                  <div class="nt-empty is-error" role="alert" *ngIf="childState(key) === 'error'">Could not load this breakdown. Collapse and expand the row to retry.</div>
                  <ng-container *ngIf="childState(key) !== 'loading' && childState(key) !== 'error'">
                    <ng-container *ngTemplateOutlet="tableTpl; context: { levelIndex: levelIndex + 1, rows: children(row, levelIndex), parentKey: key }"></ng-container>
                  </ng-container>
                </section>
              </td>
            </tr>
          </ng-container>
        </ng-container>

        <tr *ngIf="!rows.length" class="nt-row">
          <td class="nt-no-rows" [attr.colspan]="rowSpan(levelIndex)">{{ config.emptyText || 'No data to display' }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</ng-template>

<!-- One Save per table, above it: saves every edited row of that table (one
     update each). Shown once a row is edited and every mandatory (*) column of
     the edited rows is filled. -->
<ng-template #saveTpl let-levelIndex="levelIndex" let-parentKey="parentKey" let-rows="rows">
  <div class="nt-level-save" *ngIf="rowSaveOf(levelIndex) as save">
    <ng-container [ngSwitch]="tableSaveState(levelIndex, parentKey)">
      <span *ngSwitchCase="'saving'" class="nt-save-note">Saving…</span>
      <span *ngSwitchCase="'saved'" class="nt-save-note is-ok">Saved ✓</span>
      <span *ngSwitchCase="'error'" class="nt-save-note is-error">Some rows failed</span>
    </ng-container>
    <button
      *ngIf="canSaveTable(levelIndex, parentKey, rows) && tableSaveState(levelIndex, parentKey) !== 'saving'"
      type="button"
      class="nt-action is-filled"
      (click)="saveTable(levelIndex, parentKey, rows); $event.stopPropagation()"
    >
      {{ tableSaveState(levelIndex, parentKey) === 'error' ? 'Retry' : (save.label || 'Save') }} ({{ pendingKeys(parentKey, rows).length }})
    </button>
  </div>
</ng-template>

<ng-template #actionsTpl let-levelIndex="levelIndex" let-parentRow="parentRow" let-parentKey="parentKey" let-rows="rows">
  <div class="nt-actions" *ngIf="level(levelIndex)?.actions?.length">
    <button
      *ngFor="let item of level(levelIndex)?.actions"
      type="button"
      class="nt-action"
      [class.is-filled]="item.style === 'filled'"
      (click)="runAction(item.id, levelIndex, parentRow, rows, parentKey)"
    >
      <mat-icon *ngIf="item.icon">{{ item.icon }}</mat-icon>
      {{ item.label }}
    </button>
  </div>
</ng-template>
